<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="new-practice-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="new-practice-form" @submit.prevent="onSubmit">
        <div class="new-practice-form-body">
          <v-toolbar flat height="76">
            <v-toolbar-title class="new-practice-toolbar-title"
              >Add Participant</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <!-- practice -->
          <v-card-title class="new-practice-card-title pb-0"
            >User data</v-card-title
          >
          <v-container class="new-practice-card-container pt-0">
            <v-row>
              <v-col sm="6">
                <dialog-input
                  label="First name"
                  name="firstName"
                  @input="onInput"
                />
              </v-col>
              <v-col sm="6">
                <dialog-input
                  label="Last name"
                  name="lastName"
                  @input="onInput"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container class="new-practice-card-container pt-0">
            <dialog-input
              label="Email address"
              name="email"
              prepend-icon="mdi-email-outline"
              :validate="true"
              :validationType="'email'"
              @input="onInput"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0">
            <dialog-input
              label="Phone"
              name="phone"
              :validate="true"
              :validationType="isSmsChannel ? 'usPhone' : 'digits'"
              :error-messages="phoneErrorMessages"
              prepend-icon="mdi-phone-outline"
              @input="onInput"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0">
            <p class="dialog-select-label my-0">Primary channel</p>
            <v-select
              v-model="newPatientData.primaryChannel"
              :items="channelOptions"
              item-text="text"
              item-value="value"
              :item-disabled="itemDisabled"
              height="48"
              solo
              flat
              dense
              full-width
              hide-details
              @input="onInput({ name: 'primaryChannel', value: $event })"
            />
            <p
              v-if="!tenantChannelFlags.smsEnabled && !tenantChannelFlags.emailPromptsEnabled"
              class="channel-hint mt-1 mb-0"
            >
              SMS / Email channels are not enabled for this tenant.
            </p>
          </v-container>
          <v-container
            v-if="isLiteChannel"
            class="new-practice-card-container py-0"
          >
            <!--
              10DLC: the carrier reviewer reads this notice off a screenshot of
              this dialog, so it renders as plain body text — never a tooltip,
              popover, expander or placeholder. Copy lives in
              @/helpers/smsConsent so it can be diffed against the campaign
              registration.
            -->
            <div v-if="isSmsChannel" class="sms-consent-notice">
              <p class="sms-consent-notice-line">{{ smsConsentIntro }}</p>
              <p class="sms-consent-notice-line">
                {{ smsConsentFrequencyAndRates }}
              </p>
              <p class="sms-consent-notice-line">{{ smsConsentKeywords }}</p>
              <p class="sms-consent-notice-links">
                <a
                  :href="smsTermsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ smsTermsLabel }}</a
                >
                <span class="sms-consent-notice-separator">·</span>
                <a
                  :href="privacyPolicyUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ privacyPolicyLabel }}</a
                >
              </p>
            </div>
            <v-checkbox
              v-model="newPatientData.tcpaConsent"
              :label="tcpaConsentLabel"
              class="tcpa-consent"
              hide-details
              @change="onInput({ name: 'tcpaConsent', value: $event })"
            />
            <div v-if="isSmsChannel" class="sms-consent-method">
              <p class="dialog-select-label mt-2 mb-0">
                {{ smsConsentMethodLabel }}
              </p>
              <v-radio-group
                v-model="newPatientData.smsConsentMethod"
                class="sms-consent-method-group mt-1"
                hide-details
              >
                <v-radio
                  v-for="method in smsConsentMethods"
                  :key="method.value"
                  :label="method.text"
                  :value="method.value"
                />
              </v-radio-group>
            </div>
          </v-container>
          <!-- <v-container class="new-practice-card-container pb-0">
            <dialog-select
              class="mb-3"
              label="Status"
              name="status"
              v-model="newPatientData.status"
              :items="participantStatuses"
              item-text="text"
              item-value="value"
              @input="onInput"
              clearable
            />
          </v-container> -->
          <v-container class="new-practice-card-container">
            <dialog-select
              v-if="!practiceUserId"
              label="Advisor"
              name="practiceUserId"
              :items="advisors"
              item-text="fullName"
              item-value="id"
              @input="onInput"
              :value="newPatientData.practiceUserId"
            />
          </v-container>
          <v-card-title class="new-practice-card-title pb-0 d-none"
            >Feature Settings</v-card-title
          >
          <v-container class="new-practice-card-container py-0 d-none">
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.addPracticePatientDialog.swapFlag"
              v-model="newPatientData.swapAvailable"
              :items="computedFeatureFlagsSwap"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('swapAvailable', $event)"
            />
          </v-container>
          <v-container
            class="new-practice-card-container py-0 d-none"
            v-if="featuresSettings.VUE_FEATURE_SMART_SURVEY"
          >
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.addPracticePatientDialog.smartSurveyFlag"
              v-model="newPatientData.smartSurveyAvailable"
              :items="computedFeatureFlagsSmartSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('smartSurveyAvailable', $event)"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0 d-none">
            <bh-select
              class="mb-3"
              :disabled="!roleAllowedToManageJournalFlag"
              :label="tenantTexts.addPracticePatientDialog.journalFlag"
              v-model="newPatientData.journalAvailable"
              :items="computedFeatureFlagsJournal"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('journalAvailable', $event)"
            />
          </v-container>
          <v-container
            class="new-practice-card-container py-0 d-none"
            v-if="featuresSettings.VUE_FEATURE_SCREENER"
          >
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.addPracticePatientDialog.surveyFlag"
              v-model="newPatientData.surveyAvailable"
              :items="computedFeatureFlagsSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('surveyAvailable', $event)"
            />
          </v-container>
        </div>
        <v-container class="new-practice-card-footer d-flex justify-end pt-0">
          <v-btn
            :disabled="!formIsValid"
            rounded
            color="primary"
            type="submit"
            elevation="0"
          >
            Add participant
          </v-btn>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import DialogInput from '@/components/forms/DialogInput.vue'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import jwt_decode from 'jwt-decode'
import { mapState, mapGetters, mapActions } from 'vuex'
import BhSelect from '@/components/inputs/BhSelect.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'
import FeaturesSettingsMixin from '@/mixins/featuresSettings'
import { DEFAULT_LANGUAGE } from '../../helpers/constants'
import {
  isValidUsPhone,
  normalizeUsPhoneToE164,
} from '../../helpers/phoneFormat'
import {
  EMAIL_CONSENT_CHECKBOX_LABEL,
  PRIVACY_POLICY_LABEL,
  PRIVACY_POLICY_URL,
  SMS_CONSENT_CHECKBOX_LABEL,
  SMS_CONSENT_DISCLOSURE,
  SMS_CONSENT_FREQUENCY_AND_RATES,
  SMS_CONSENT_INTRO,
  SMS_CONSENT_KEYWORDS,
  SMS_CONSENT_METHODS,
  SMS_CONSENT_METHOD_LABEL,
  SMS_CONSENT_VERSION,
  SMS_TERMS_LABEL,
  SMS_TERMS_URL,
} from '../../helpers/smsConsent'

const emptyForm = {
  firstName: '',
  lastName: '',
  address: '',
  practiceId: 0,
  email: '',
  isActive: true,
  phone: '',
  practiceUserId: null,
  journalAvailable: 'Active',
  smartSurveyAvailable: 'Active',
  swapAvailable: 'Active',
  surveyAvailable: 'Inactive',
  language: DEFAULT_LANGUAGE,
  primaryChannel: 'MOBILE',
  // Consent is opt-in only: these two never get a default, and they are not
  // part of any select-all / bulk-default behaviour.
  tcpaConsent: false,
  smsConsentMethod: null,
}

export default {
  name: 'NewPracticePatientDialog',
  components: {
    DialogInput,
    DialogSelect,
    BhSelect,
  },
  mixins: [TenantTextsMixin, FeaturesSettingsMixin],
  props: {
    swapAvailable: {
      type: String,
      default: 'Active',
    },
    smartSurveyAvailable: {
      type: String,
      default: 'Active',
    },
    journalAvailable: {
      type: String,
      default: 'Active',
    },
    surveyAvailable: {
      type: String,
      default: 'Inactive',
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    practiceId: {
      type: Number,
      default: 0,
    },
    practiceUserId: {
      type: [Number, null],
      default: () => null,
    },
    tenantChannelFlags: {
      type: Object,
      default: () => ({ smsEnabled: false, emailPromptsEnabled: false }),
    },
  },
  data() {
    return {
      dialog: false,
      newPatientData: {
        ...emptyForm,
        practiceId: this.practiceId,
        practiceUserId: this.practiceUserId,
        journalAvailable: this.journalAvailable || emptyForm.journalAvailable,
        smartSurveyAvailable:
          this.smartSurveyAvailable || emptyForm.smartSurveyAvailable,
        swapAvailable: this.swapAvailable || emptyForm.swapAvailable,
        surveyAvailable: this.surveyAvailable || emptyForm.surveyAvailable,
      },
      rules: {
        text: [v => !!v || 'Value cannot by empty'], // @todo
      },
      practiceDoctors: [],
    }
  },
  watch: {
    swapAvailable: function(val) {
      if (val) {
        this.newPatientData.swapAvailable = val
      }
    },
    smartSurveyAvailable: function(val) {
      if (val) {
        this.newPatientData.smartSurveyAvailable = val
      }
    },
    journalAvailable: function(val) {
      if (val) {
        this.newPatientData.journalAvailable = val
      }
    },
    surveyAvailable: function(val) {
      if (val) {
        this.newPatientData.surveyAvailable = val
      }
    },
    'newPatientData.primaryChannel': function() {
      // Consent belongs to the channel it was given for. Changing the channel
      // invalidates it, so nothing stale can ride along into the payload.
      this.newPatientData.tcpaConsent = false
      this.newPatientData.smsConsentMethod = null
    },
    async showDialog(val) {
      if (val) {
        this.dialog = true
        const doctors = await this.getPracticeUsers({
          id: this.practiceId,
          params: this.doctorsQuery,
          returnData: true,
        })

        this.practiceDoctors = doctors || []
      }
    },
  },
  computed: {
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
      'dictionaryFeatureFlagState',
    ]),
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    isAnyAdminRole() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    participantStatuses() {
      return [
        { text: 'Tech Challenged', value: 'TECH_CHALLENGED' },
        { text: 'Home Challenged', value: 'HOME_CHALLENGED' },
        { text: 'Communication Challenged', value: 'COMMUNICATION_CHALLENGED' },
        { text: 'Resistance to Change', value: 'RESISTANCE_TO_CHANGE' },
        { text: 'Actively Changing', value: 'ACTIVELY_CHANGING' },
      ]
    },
    roleAllowedToManageJournalFlag() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor
      )
    },
    computedFeatureFlagsJournal() {
      return [
        {
          text: 'Journal & Metrics',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Journal only',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Partial
          ],
        },
        {
          text: 'Journal off',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSmartSurvey() {
      return [
        {
          text: 'Full survey',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Initial questions only',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Partial
          ],
        },
        {
          text: 'Survey off',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSwap() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSurvey() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    isLiteChannel() {
      const c = this.newPatientData.primaryChannel
      return c === 'SMS' || c === 'EMAIL'
    },
    isSmsChannel() {
      return this.newPatientData.primaryChannel === 'SMS'
    },
    smsConsentIntro() {
      return SMS_CONSENT_INTRO
    },
    smsConsentFrequencyAndRates() {
      return SMS_CONSENT_FREQUENCY_AND_RATES
    },
    smsConsentKeywords() {
      return SMS_CONSENT_KEYWORDS
    },
    smsTermsLabel() {
      return SMS_TERMS_LABEL
    },
    smsTermsUrl() {
      return SMS_TERMS_URL
    },
    privacyPolicyLabel() {
      return PRIVACY_POLICY_LABEL
    },
    privacyPolicyUrl() {
      return PRIVACY_POLICY_URL
    },
    smsConsentMethodLabel() {
      return SMS_CONSENT_METHOD_LABEL
    },
    smsConsentMethods() {
      return SMS_CONSENT_METHODS
    },
    phoneErrorMessages() {
      // Only the SMS channel gets the stricter US-number check; MOBILE/EMAIL
      // keep the legacy digits-only validation so their flows do not change.
      const phone = this.newPatientData.phone
      if (!this.isSmsChannel || !phone) return []
      return isValidUsPhone(phone)
        ? []
        : ['Enter a valid 10-digit US mobile number, e.g. (206) 555-0147.']
    },
    channelOptions() {
      return [
        { text: 'Mobile app', value: 'MOBILE', disabled: false },
        {
          text: this.tenantChannelFlags.smsEnabled
            ? 'SMS'
            : 'SMS (not enabled for tenant)',
          value: 'SMS',
          disabled: !this.tenantChannelFlags.smsEnabled,
        },
        {
          text: this.tenantChannelFlags.emailPromptsEnabled
            ? 'Email'
            : 'Email (not enabled for tenant)',
          value: 'EMAIL',
          disabled: !this.tenantChannelFlags.emailPromptsEnabled,
        },
      ]
    },
    itemDisabled() {
      return item => !!item.disabled
    },
    tcpaConsentLabel() {
      return this.isSmsChannel
        ? SMS_CONSENT_CHECKBOX_LABEL
        : EMAIL_CONSENT_CHECKBOX_LABEL
    },
    formIsValid() {
      const d = this.newPatientData
      // Email is ALWAYS required (per user instruction — even for SMS).
      // Phone is required when channel = SMS.
      // TCPA consent is required when channel != MOBILE.
      const baseValid =
        d.firstName &&
        d.lastName &&
        d.email &&
        d.practiceUserId &&
        d.journalAvailable &&
        d.smartSurveyAvailable &&
        d.swapAvailable &&
        d.surveyAvailable
      // SMS needs a number we can actually normalize to E.164 before sending.
      const phoneValid = this.isSmsChannel ? isValidUsPhone(d.phone) : true
      // Legacy callers always required phone — keep that bar for MOBILE/EMAIL
      // to avoid regressing the existing happy-path.
      const legacyPhoneValid =
        d.primaryChannel === 'MOBILE' ? !!d.phone : phoneValid
      const consentValid = this.isLiteChannel ? d.tcpaConsent === true : true
      // Consent is not enough on its own — the audit record has to say how it
      // was collected, so submit stays disabled until a method is picked.
      const consentMethodValid = this.isSmsChannel
        ? !!d.smsConsentMethod
        : true
      return (
        baseValid && legacyPhoneValid && consentValid && consentMethodValid
      )
    },
    advisors() {
      let advisorList = []
      if (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      ) {
        let doctor = this.practiceDoctors.find(
          doctor => doctor.id == this.practiceUserId,
        )
        if (doctor) {
          doctor.fullName = doctor.firstName + ' ' + doctor.lastName
          advisorList.push(doctor)
        }
      } else {
        advisorList = this.practiceDoctors.map(doctor => ({
          ...doctor,
          fullName: `${doctor.firstName} ${doctor.lastName}`,
        }))
      }
      return advisorList
    },
    doctorQueryParams() {
      return {
        roleIds: [
          this.dictionaryPracticeUserRole.Advisor,
          this.dictionaryBhUsersRoleIds.Consultant,
        ],
        isActive: true,
        sortBy: 'fullName',
        isAvailable: true,
      }
    },
    doctorsQuery() {
      const params = new URLSearchParams()
      for (const [key, value] of Object.entries(this.doctorQueryParams)) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params.append(key, item)
          })
        } else if (value) {
          params.append(key, value)
        }
      }
      return params
    },
  },
  methods: {
    ...mapActions('practicesStates', ['getPracticeUsers']),
    closeDialog() {
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    changeFeatureFlag(key, value) {
      this.onInput({ name: key, value })
    },
    onInput({ name, value }) {
      this.newPatientData[name] = value
    },
    clearForm() {
      this.$refs.form.reset()
      this.newPatientData = {
        ...emptyForm,
        practiceId: this.practiceId,
        practiceUserId: this.practiceUserId,
        journalAvailable: this.journalAvailable || emptyForm.journalAvailable,
        smartSurveyAvailable:
          this.smartSurveyAvailable || emptyForm.smartSurveyAvailable,
        swapAvailable: this.swapAvailable || emptyForm.swapAvailable,
        surveyAvailable: this.surveyAvailable || emptyForm.surveyAvailable,
      }
    },
    buildSubmitPayload() {
      const d = this.newPatientData
      const payload = { ...d }
      // Strip the UI-only checkbox; replace with channel-specific consent flag
      // the backend records (server-side timestamps the actual opt-in).
      delete payload.tcpaConsent
      // Re-added below for SMS only, so a channel switch cannot leak it.
      delete payload.smsConsentMethod
      if (d.primaryChannel === 'SMS') {
        payload.phone = normalizeUsPhoneToE164(d.phone)
        payload.smsConsent = d.tcpaConsent === true
        if (d.tcpaConsent === true) {
          // Record what the advisor was shown, not just that a box was ticked:
          // the exact disclosure text and its version are the audit trail we
          // can hold up against the 10DLC campaign registration.
          payload.smsConsentVersion = SMS_CONSENT_VERSION
          payload.smsConsentText = SMS_CONSENT_DISCLOSURE
          payload.smsConsentMethod = d.smsConsentMethod
          // Corroborating only — the server's own opt-in timestamp stays
          // authoritative.
          payload.smsConsentCapturedAt = new Date().toISOString()
        }
      } else if (d.primaryChannel === 'EMAIL') {
        payload.emailConsent = d.tcpaConsent === true
      }
      return payload
    },
    onSubmit() {
      this.$emit('on-submit', this.buildSubmitPayload())
      this.clearForm()
      this.closeDialog()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.channel-hint {
  font-size: 0.75rem;
  color: $bh-gray--2;
}

// 10DLC opt-in disclosure. Deliberately not tenant-tinted: this block is filed
// as carrier evidence and has to stay high-contrast and legible in a PNG, so no
// opacity dimming, no colour-only link styling and nothing below 13px.
.sms-consent-notice {
  margin-top: 4px;
  padding: 12px 14px;
  border: 1px solid $bh-gray--3;
  border-radius: $border-radius-root;
  background-color: #ffffff;
  color: $bh-black;

  &-line {
    margin: 0 0 8px;
    font-size: 13px;
    line-height: 1.45;
  }

  &-links {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;

    a {
      color: $bh-black;
      font-weight: 600;
      text-decoration: underline;
    }
  }

  &-separator {
    padding: 0 4px;
  }
}

.tcpa-consent {
  margin-top: 4px;

  .v-label {
    height: auto;
    font-size: 13px;
    line-height: 1.35;
    white-space: normal;
    color: $bh-black;
  }
}

.sms-consent-method {
  .v-radio {
    margin-bottom: 2px;
  }

  .v-label {
    height: auto;
    font-size: 13px;
    line-height: 1.35;
    color: $bh-black;
  }

  &-group {
    padding-top: 0;
  }
}

.new-practice {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-form {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
